import { getAccessToken } from '../auth/tokenProvider';
import { getApiUrl } from './config';

const buildUrl = (url: string, params?: Record<string, any>): string => {
    if (!params) return url;
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
};

const fetchWithAuth = async (url: string, method: string = "GET", body: BodyInit | null = null, _retried: boolean = false) => {
    const headers: Record<string, string> = {};

    const token = await getAccessToken(_retried ? true : undefined);
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    let serializedBody: BodyInit | null;
    if (body instanceof FormData) {
        serializedBody = body;
    } else {
        headers['Content-Type'] = 'application/json';
        serializedBody = body ? JSON.stringify(body) : null;
    }

    const options: RequestInit = {
        method,
        headers,
        body: serializedBody ?? undefined,
    };

    try {
        const finalUrl = `${getApiUrl()}${url}`;
        const response = await fetch(finalUrl, options);

        // On 401, force-refresh the token and retry once.
        // This handles the case where MSAL returned a cached token that expired
        // during backend processing (planning + agent invocation can take minutes).
        if (response.status === 401 && !_retried) {
            return fetchWithAuth(url, method, body, true);
        }

        if (!response.ok) {
            const errorText = await response.text();
            const err = new Error(errorText || 'Something went wrong') as any;
            err.status = response.status;
            throw err;
        }

        const isJson = response.headers.get('content-type')?.includes('application/json');
        return isJson ? await response.json() : null;
    } catch (error: any) {
        if (error?.status !== 404) {
            console.info('API Error:', (error as Error).message);
        }
        throw error;
    }
};

export const apiClient = {
    get: (url: string, config?: { params?: Record<string, any> }) =>
        fetchWithAuth(buildUrl(url, config?.params), 'GET'),
    post: (url: string, body?: any) => fetchWithAuth(url, 'POST', body),
    put: (url: string, body?: any) => fetchWithAuth(url, 'PUT', body),
    delete: (url: string) => fetchWithAuth(url, 'DELETE'),
    upload: (url: string, formData: FormData) => fetchWithAuth(url, 'POST', formData),
};
